/**
 * Bậc ảnh đang hiện + bậc đang tải ngầm của Bộ soi ảnh.
 *
 * Đổi bậc KHÔNG nháy: bậc đang hiện giữ nguyên tới khi bậc mới tải xong (thẻ `<img>` thứ hai, ẩn),
 * rồi mới thế chỗ. Tải hỏng thì ghi bậc đó vào sổ hỏng, giữ bậc cũ và NÓI RA — không để người xem
 * tưởng mình đang xem ảnh gốc (cùng tinh thần `useOriginalLoad` ở màn Sửa theo lần).
 *
 * Nhớ theo LINK, không theo chỉ số: sang tấm khác là link khác, tự về trạng thái đầu.
 */

import { useState } from "react";
import { pickRung, type PhotoLadder, type Rung } from "./ladder";

export type LadderImage = {
  /** Bậc đang hiện. */
  shown: Rung | undefined;
  /** Bậc nét hơn đang tải ngầm (chưa xong). */
  pending: Rung | undefined;
  /** Dòng báo nhỏ ở góc khung; null = không có gì để báo. */
  note: string | null;
  /** Gọi từ `onLoad` của ảnh đang hiện: bậc đầu vẽ xong cũng phải ghi là đã tải. */
  onShownLoad: () => void;
  onShownError: () => void;
  onPendingLoad: () => void;
  onPendingError: () => void;
};

export function useLadderImage(ladder: PhotoLadder, needed: number): LadderImage {
  const [broken, setBroken] = useState<ReadonlySet<string>>(() => new Set());
  const [loaded, setLoaded] = useState<ReadonlySet<string>>(() => new Set());
  const usable: PhotoLadder = { ...ladder, rungs: ladder.rungs.filter((r) => !broken.has(r.url)) };
  const target = pickRung(usable, needed);

  // Bậc đang hiện: đã có bậc ĐÃ TẢI đủ nét thì dùng bậc nhỏ nhất trong số đó (không tải gì thêm);
  // chưa có thì giữ bậc đã tải nét nhất trong lúc tải bậc cần; chưa bậc nào tải xong thì hiện
  // thẳng bậc cần (lần vẽ đầu — không có gì để giữ).
  const loadedRungs = usable.rungs.filter((r) => loaded.has(r.url));
  const enough = target ? loadedRungs.find((r) => r.longEdge >= target.longEdge) : undefined;
  const shown = enough ?? loadedRungs.at(-1) ?? target;
  const pending = target && shown && shown.longEdge < target.longEdge ? target : undefined;

  // Bản gốc hỏng (link hết hạn, định dạng trình duyệt không vẽ được) mà người xem vẫn cần nét hơn
  // bậc đang có ⇒ NÓI RA, kể cả khi đang tự lùi sang bậc kế tiếp.
  const originalFailed = ladder.rungs.some((r) => r.isOriginal && broken.has(r.url)) && needed > (shown?.longEdge ?? 0);
  const note = originalFailed
    ? pending
      ? `Không tải được bản gốc — đang tải ${pending.label}`
      : `Không tải được bản gốc — đang xem ${shown?.label ?? "bản nhỏ"}`
    : pending
      ? pending.isOriginal
        ? "Đang tải bản gốc…"
        : "Đang tải bản nét hơn…"
      : null;

  const mark = (set: typeof setLoaded, url: string | undefined) => {
    if (!url) return;
    set((prev) => (prev.has(url) ? prev : new Set(prev).add(url)));
  };

  return {
    shown,
    pending,
    note,
    onShownLoad: () => mark(setLoaded, shown?.url),
    // Bậc đang hiện hỏng (link hết hạn, kho trả lỗi) ⇒ bỏ bậc đó, lùi bậc khác.
    onShownError: () => mark(setBroken, shown?.url),
    onPendingLoad: () => mark(setLoaded, pending?.url),
    onPendingError: () => mark(setBroken, pending?.url),
  };
}
